import { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import { formatCurrency, formatDateParam, useTranslate } from "helpers";
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";
import {
  getFleetVehicleOfferRentsAPI,
  getFleetVehicleOfferSalesAPI,
  getOfferRentStatusesAPI,
  getOfferSaleStatusesAPI,
} from "services/api/fleet/vehicles/offers/api";
import { initialPagination } from "ui/components/Table/consts";
import {
  FleetVehicleOfferRentRow,
  FleetVehicleOfferSaleRow,
  OfferRentStatus,
  OfferSaleStatus,
} from "services/api/fleet/vehicles/offers/types";
import { getOfferStatusBarFill, getOfferStatusRingColor, getOfferStatusAmountColor } from "services/api/offers/consts";
import Sparkline from "../../../common/Sparkline";
import DashboardTooltip, { TooltipDivider, TooltipRow, TooltipTitle } from "../../../common/DashboardTooltip";
import BarTooltip, { BarTooltipRow, BarTooltipTitle } from "../../../common/HoverPopup";
import CardHeader from "../../../common/CardHeader";
import { primary, text } from "../../../common/dashboardPalette";
import { OfferStats, OfferStatusCategory } from "./types";
import { useDashboardDateRange } from "../../../../context/DashboardDateRangeContext";
import { inRange } from "services/api/dashboard/range";
import { rangeLabel } from "ui/components/DateRangePicker";

type OfferType = "sale" | "rent";

// Redoslijed statusa u pipeline-u (predložak -> ... -> otkazana). Nepoznati idu na kraj.
const STATUS_ORDER = ["draft", "created", "sent", "accepted", "paid", "cancelled"];
const statusRank = (id: string) => {
  const i = STATUS_ORDER.indexOf(id);
  return i === -1 ? 999 : i;
};

interface AggregatedOffer {
  statusId: string;
  total: number;
  type: OfferType;
}

const OfferStatsTab: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { range } = useDashboardDateRange();

  // Klik na status-red -> pretraga ponuda po tom statusu + trenutnom datumskom rasponu widgeta.
  // Landa na tab "Prodaja"; isti `status` param čita i tab "Najam".
  const handleRowClick = (statusId: string) => {
    const params = new URLSearchParams({ tab: "sale", status: statusId });
    const from = formatDateParam(range.from);
    const to = formatDateParam(range.to);
    if (from) params.set("offerDateFrom", from);
    if (to) params.set("offerDateTo", to);
    navigate(`${FinanceRoutes.OfferSearchPageRouter}?${params.toString()}`);
  };

  const [stats, setStats] = useState<OfferStats>({
    totalCount: 0,
    totalValue: 0,
    totalSaleCount: 0,
    totalRentCount: 0,
    totalSaleValue: 0,
    totalRentValue: 0,
    avgValue: 0,
    inProgressCount: 0,
    inProgressValue: 0,
    monthlyTrend: [],
    categories: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchOfferStats = async () => {
    try {
      setIsLoading(true);

      const [saleStatusesResult, rentStatusesResult, salesResult, rentsResult] = await Promise.all([
        getOfferSaleStatusesAPI(),
        getOfferRentStatusesAPI(),
        getFleetVehicleOfferSalesAPI({ ...initialPagination, pageSize: 1000 }),
        getFleetVehicleOfferRentsAPI({ ...initialPagination, pageSize: 1000 }),
      ]);

      const saleStatuses = saleStatusesResult as OfferSaleStatus[];
      const rentStatuses = rentStatusesResult as OfferRentStatus[];
      // Filtriranje po globalnom datumskom rasponu (offerDate).
      const sales = salesResult.data.filter((o: FleetVehicleOfferSaleRow) =>
        inRange(o.offerDate ? new Date(o.offerDate) : null, range));
      const rents = rentsResult.data.filter((o: FleetVehicleOfferRentRow) =>
        inRange(o.offerDate ? new Date(o.offerDate) : null, range));

      const aggregated: (AggregatedOffer & { date: Date | null })[] = [
        ...sales.map((o: FleetVehicleOfferSaleRow) => ({
          statusId: o.status?.id || "unknown",
          total: o.total || 0,
          type: "sale" as OfferType,
          date: o.offerDate ? new Date(o.offerDate) : null,
        })),
        ...rents.map((o: FleetVehicleOfferRentRow) => ({
          statusId: o.status?.id || "unknown",
          total: o.total || 0,
          type: "rent" as OfferType,
          date: o.offerDate ? new Date(o.offerDate) : null,
        })),
      ];

      const totalCount = aggregated.length;
      const totalValue = aggregated.reduce((sum, o) => sum + o.total, 0);
      const totalSaleCount = sales.length;
      const totalRentCount = rents.length;
      const totalSaleValue = sales.reduce((sum: number, o: FleetVehicleOfferSaleRow) => sum + (o.total || 0), 0);
      const totalRentValue = rents.reduce((sum: number, o: FleetVehicleOfferRentRow) => sum + (o.total || 0), 0);

      const grouped = aggregated.reduce(
        (acc: Record<string, { count: number; total: number; saleCount: number; rentCount: number; saleTotal: number; rentTotal: number }>, o) => {
          if (!acc[o.statusId]) {
            acc[o.statusId] = { count: 0, total: 0, saleCount: 0, rentCount: 0, saleTotal: 0, rentTotal: 0 };
          }
          acc[o.statusId].count += 1;
          acc[o.statusId].total += o.total;
          if (o.type === "sale") {
            acc[o.statusId].saleCount += 1;
            acc[o.statusId].saleTotal += o.total;
          } else {
            acc[o.statusId].rentCount += 1;
            acc[o.statusId].rentTotal += o.total;
          }
          return acc;
        },
        {}
      );

      // Status definicije: prvi match po ID-u (sale ima prioritet pa rent kao fallback).
      // Sale i rent koriste iste statusId-eve (draft/sent/accepted/paid/cancelled), pa labela ostaje konzistentna.
      const findStatus = (id: string) =>
        saleStatuses.find((s) => s.id === id) || rentStatuses.find((s) => s.id === id);

      const categories: OfferStatusCategory[] = Object.entries(grouped)
        .map(([statusId, data]) => {
          const status = findStatus(statusId);
          return {
            statusId,
            label: status?.name || statusId,
            count: data.count,
            total: data.total,
            percentage: totalCount > 0 ? Math.round((data.count / totalCount) * 100) : 0,
            color: status?.color || "blue",
            saleCount: data.saleCount,
            rentCount: data.rentCount,
            saleTotal: data.saleTotal,
            rentTotal: data.rentTotal,
          };
        })
        .sort((a, b) => statusRank(a.statusId) - statusRank(b.statusId));

      // U tijeku = aktivne ponude (poslano + prihvaćeno) — još nisu plaćene ni otkazane.
      const inProgress = aggregated.filter((o) =>
        o.statusId === "sent" || o.statusId === "accepted"
      );
      const inProgressCount = inProgress.length;
      const inProgressValue = inProgress.reduce((s, o) => s + o.total, 0);
      const avgValue = totalCount > 0 ? totalValue / totalCount : 0;

      // Mjesečni trend zadnjih 6 mjeseci (broj ponuda po mjesecu).
      const now = new Date();
      const monthlyTrend = Array.from({ length: 6 }, (_, i) => {
        const monthStart = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() - (5 - i) + 1, 1);
        return aggregated.filter((o) => o.date && o.date >= monthStart && o.date < monthEnd).length;
      });

      setStats({
        totalCount,
        totalValue,
        totalSaleCount,
        totalRentCount,
        totalSaleValue,
        totalRentValue,
        avgValue,
        inProgressCount,
        inProgressValue,
        monthlyTrend,
        categories,
      });
    } catch (error) {
      if ((error as any)?.response?.status !== 401) console.error("Error fetching offer stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOfferStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  if (isLoading) {
    return (
      <div className="relative h-full min-h-[200px]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  if (stats.categories.length === 0) {
    return (
      <NoResults
        message={t("admin.dashboard.widget.offers.stats.noresults")}
      />
    );
  }

  return (
    <div className="rounded-sm px-4 py-5 bg-white">
      <CardHeader
        title={t("admin.dashboard.widget.offers.tab.stats.title")}
        subtitle={rangeLabel(range)}
        className="mb-10"
        right={
          <DashboardTooltip
            trigger={
              <div className="flex flex-col items-end cursor-help">
                <div className="flex items-center gap-2">
                  <Sparkline values={stats.monthlyTrend} stroke={primary.hex} fill={primary.hexSoft} />
                  <span className={`text-2xl font-bold ${text.primary}`}>
                    {formatCurrency.format(stats.totalValue)}
                  </span>
                </div>
                <span className={`text-sm ${text.muted}`}>⌀ {formatCurrency.format(stats.avgValue)} po ponudi</span>
              </div>
            }
          >
          <TooltipTitle>Pregled ponuda</TooltipTitle>
          <TooltipRow label="Prodaja" value={`${formatCurrency.format(stats.totalSaleValue)} (${stats.totalSaleCount})`} />
          <TooltipRow label="Najam" value={`${formatCurrency.format(stats.totalRentValue)} (${stats.totalRentCount})`} />
          <TooltipDivider />
          <TooltipRow label="Ukupno" value={`${formatCurrency.format(stats.totalValue)} (${stats.totalCount})`} emphasis="positive" />
          {stats.inProgressCount > 0 && (
            <TooltipRow label="U tijeku" value={`${stats.inProgressCount} · ${formatCurrency.format(stats.inProgressValue)}`} />
          )}
          </DashboardTooltip>
        }
      />

      <div className="space-y-2">
        {stats.categories.map((category) => (
          <div
            key={category.statusId}
            className={`group/row relative rounded-sm ring-1 ring-slate-200 bg-white cursor-pointer transition-all hover:ring-2 hover:shadow-sm ${getOfferStatusRingColor(category.statusId)}`}
            onClick={() => handleRowClick(category.statusId)}
          >
            {/* Ispuna proporcionalna udjelu — podaci stoje UNUTAR bara.
                overflow-hidden je na unutarnjem wrapperu da ne reže tooltip iznad reda. */}
            <div className="absolute inset-0 overflow-hidden rounded-sm">
              <div
                className={`absolute inset-y-0 left-0 origin-left animate-growRight transition-all duration-500 ${getOfferStatusBarFill(category.statusId)}`}
                style={{ width: `${Math.max(category.percentage, 5)}%` }}
              />
            </div>
            <div className="relative flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{category.label}</div>
                <div className="text-xs text-slate-500 truncate">
                  {category.count} · {category.saleCount} prodaja / {category.rentCount} najam
                </div>
              </div>
              <div className="flex flex-col items-end flex-shrink-0">
                <span className={`text-sm font-bold ${getOfferStatusAmountColor(category.statusId)}`}>{formatCurrency.format(category.total)}</span>
                <span className="text-xs text-slate-500">{category.percentage}%</span>
              </div>
            </div>
            <BarTooltip groupName="row" align="right">
              <BarTooltipTitle>{category.label}</BarTooltipTitle>
              <BarTooltipRow label="Prodaja" value={`${category.saleCount} · ${formatCurrency.format(category.saleTotal)}`} />
              <BarTooltipRow label="Najam" value={`${category.rentCount} · ${formatCurrency.format(category.rentTotal)}`} />
            </BarTooltip>
          </div>
        ))}
      </div>

      <div className="mt-4 text-sm">
        <div className={text.muted}>
          {stats.totalCount}{" "}
          {t("admin.dashboard.widget.offers.stats.totaloffers")}
          {" · "}
          <span className={text.subtle}>{stats.totalSaleCount} prodaja / {stats.totalRentCount} najam</span>
        </div>
        {stats.inProgressCount > 0 && (
          <div className={`${primary.text} mt-1`}>
            <span className="font-medium">{stats.inProgressCount}</span> u tijeku
            <span className={text.subtle}>{" "}({formatCurrency.format(stats.inProgressValue)})</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(OfferStatsTab);
